// src/pages/SignUp.jsx
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IoEyeOffOutline, IoEyeOutline } from "react-icons/io5";
import { useAuthContext } from '../context';
import toast from 'react-hot-toast';
import { Country, State, City } from 'country-state-city';
import { Helmet } from "react-helmet";

const SignUp = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { SignUp, token } = useAuthContext();

  const [show, setShow] = useState(false);
  const [password, setPassword] = useState("");
  const [data, setData] = useState({});
  const [countries] = useState(Country.getAllCountries() || []);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const scrollToTop = () =>
    window.scrollTo({ top: 0, behavior: "smooth" });

  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (loading) return; // Prevent double-clicks
  
    const phoneRegex = /^\+?\d{10,15}$/;
    if (!phoneRegex.test(data.mobile)) {
      alert("Please enter a valid phone number (10–15 digits).");
      return;
    }
  
    const {
      firstName, lastName, email, mobile,
      gender, interestedIn, country, state, city,
      age, password: userPass
    } = data;
  
    if (
      !firstName || !lastName || !email || !mobile ||
      !gender || !interestedIn || !country || !state || !city ||
      !age || !userPass || !password
    ) {
      return toast.error("Please fill in all fields.");
    }
  
    if (userPass !== password) return toast.error("Passwords do not match");
    if (age < 18) return toast.error("You must be at least 18 years old to register.");
  
    const userData = {
      ...data,
      type: "user",
    };
  
    try {
      setLoading(true);
      await SignUp(userData);
    } catch (err) {
      console.error(err);
      toast.error("Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  // when country changes, update states list
  useEffect(() => {
    if (data.country) {
      setStates(State.getStatesOfCountry(data.country) || []);
      setData(prev => ({ ...prev, state: "", city: "" }));
      setCities([]);
    } else {
      setStates([]);
      setCities([]);
      setData(prev => ({ ...prev, state: "", city: "" }));
    }
  }, [data.country]);

  // when state changes, update cities
  useEffect(() => {
    if (data.state) {
      setCities(City.getCitiesOfState(data.country, data.state) || []);
      setData(prev => ({ ...prev, city: "" }));
    } else {
      setCities([]);
      setData(prev => ({ ...prev, city: "" }));
    }
  }, [data.state, data.country]);

  // after signup success, redirect
  useEffect(() => {
    let id;
    if (token) {
      id = setTimeout(() => {
        navigate(location?.state?.from?.pathname ?? "/dashboard", { replace: true });
      }, 1000);
    }
    return () => clearTimeout(id);
  }, [token, navigate, location]);

  return (
    <>
  <Helmet>
  <title>Sign Up – SomeOne4u</title>
  <meta name="description" content="Create your free SomeOne4u account and start connecting with compatible singles worldwide. Real people. Real love." />
  <meta name="keywords" content="SomeOne4u signup, dating signup, online dating registration, find love, global dating, create dating profile" />
  <meta name="author" content="SomeOne4u Team" />
  <meta name="application-name" content="SomeOne4u" />
  <link rel="canonical" href="https://someone4u.com/signup" />
 
  {/* Open Graph */}
  <meta property="og:title" content="Sign Up – SomeOne4u" />
  <meta property="og:description" content="Join the world’s trusted dating platform. Create your SomeOne4u profile and start building real connections." />
  <meta property="og:url" content="https://someone4u.com/signup" />
  <meta property="og:type" content="website" />
  <meta property="og:site_name" content="SomeOne4u" />
  <meta property="og:image" content="https://someone4u.com/image/splash.png" />
  <meta property="og:image:alt" content="SomeOne4u signup illustration" />
 
  {/* Twitter Card */}
  {/* <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="Sign Up – SomeOne4u" />
  <meta name="twitter:description" content="Create your SomeOne4u account now and meet genuine people for real relationships." />
  <meta name="twitter:image" content="https://someone4u.com/image/splash.png" /> */}
</Helmet>

    <div className="min-h-screen bg-gray-100 flex justify-center items-center py-12 px-4">
      <div className="bg-white w-full max-w-2xl rounded-xl shadow-xl p-8 border-t-4 border-red-400">
        <h1 className="text-3xl font-extrabold text-red-500 text-center mb-8">Join SomeOne4u</h1>

        <form onSubmit={handleSubmit} className="grid sm:grid-cols-2 gap-6">
          {/* your existing inputs */}
          <InputField label="First Name" name="firstName" placeholder="First Name" setData={setData} />
          <InputField label="Last Name"  name="lastName"  placeholder="Last Name"  setData={setData} />
          <InputField label="Email"      name="email"     type="email" placeholder="Email" setData={setData} />
          <InputField label="Mobile No." name="mobile"    placeholder="Mobile Number" setData={setData} />
          <InputField label="Age"        name="age"       type="number" placeholder="Age" min={18} max={100} setData={setData} />

          <SelectField
            label="I'm a"
            name="gender"
            value={data.gender || ""}
            options={[
              { value: "", label: "Select Gender" },
              { value: "Male",   label: "Male" },
              { value: "Female", label: "Female" }
            ]}
            setData={setData}
          />

          <SelectField
            label="Interested in"
            name="interestedIn"
            value={data.interestedIn || ""}
            options={[
              { value: "", label: "Looking for" },
              { value: "Male",   label: "Male" },
              { value: "Female", label: "Female" }
            ]}
            setData={setData}
          />

          {/* ← NEW: Country selector */}
          <SelectField
            label="Country"
            name="country"
            value={data.country || ""}
            options={[
              { value: "", label: "Select Country" },
              ...countries.map(c => ({ value: c.isoCode, label: c.name }))
            ]}
            setData={setData}
          />

          {/* State & City now depend on selected country */}
          <SelectField
            label="State / Region"
            name="state"
            value={data.state || ""}
            options={[
              { value: "", label: "Select State" },
              ...states.map(s => ({ value: s.isoCode, label: s.name }))
            ]}
            setData={setData}
          />

          <SelectField
            label="City"
            name="city"
            value={data.city || ""}
            options={[
              { value: "", label: "Select City" },
              ...cities.map(c => ({ value: c.name, label: c.name }))
            ]}
            setData={setData}
          />

          {/* Password fields */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              className="w-full border border-gray-300 rounded-md px-4 py-2 outline-none focus:ring-2 focus:ring-blue-500"
              type={show ? "text" : "password"}
              placeholder="Password"
              onChange={e => setData(prev => ({ ...prev, password: e.target.value }))}
              required
            />
            {show
              ? <IoEyeOffOutline className="absolute top-[60%] right-3 cursor-pointer text-gray-600" onClick={() => setShow(false)} />
              : <IoEyeOutline    className="absolute top-[60%] right-3 cursor-pointer text-gray-600" onClick={() => setShow(true)} />
            }
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
            <input
              className="w-full border border-gray-300 rounded-md px-4 py-2 outline-none focus:ring-2 focus:ring-blue-500"
              type={show ? "text" : "password"}
              placeholder="Confirm Password"
              onChange={e => setPassword(e.target.value)}
              required
            />
          </div>

          <div className="col-span-2">
          <button
  type="submit"
  onClick={handleSubmit}
  disabled={loading}
  className={`w-full bg-blue-500 text-white py-2 rounded-md font-semibold transition duration-200 
    ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-600'}`}
>
  {loading ? "Signing up..." : "Sign Up"}
</button>
          </div>
        </form>

        <p className="mt-6 text-center text-sm">
          Already have an account?{" "}
          <span
            className="text-blue-600 font-medium cursor-pointer hover:underline"
            onClick={() => {
              navigate("/login");
              scrollToTop();
            }}
          >
            Sign In
          </span>
        </p>
      </div>
    </div>
    </>
  );
};

// InputField
const InputField = ({ label, name, type = "text", placeholder, setData, ...props }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <input
      type={type}
      name={name}
      placeholder={placeholder}
      onChange={e => setData(prev => ({ ...prev, [name]: e.target.value }))}
      className="w-full border border-gray-300 rounded-md px-4 py-2 outline-none focus:ring-2 focus:ring-blue-500"
      {...props}
    />
  </div>
);

// SelectField
const SelectField = ({ label, name, value, options, setData }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <select
      name={name}
      value={value}
      onChange={e => setData(prev => ({ ...prev, [name]: e.target.value }))}
      className="w-full border border-gray-300 px-3 py-2 rounded"
      required
    >
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

export default SignUp;
